// src/screens/LoginScreen.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import './parts/LoginScreen.styles.css'; // Import the separate CSS file
import { Checkbox } from 'primereact/checkbox';
import { Message } from 'primereact/message';
import { Toast } from 'primereact/toast';
import { useAuth } from '../../hooks/useAuth';
import { useSchoolApiUrlSetup } from '../../stateManagers/hooks/useSchoolApiUrlSetup';
import { GlobalDropdownField } from '../../components/GlobalComponents/GlobalDropdownField';
import { GlobalButton } from '../../components/GlobalComponents/GlobalButton';
import { GlobalInputField } from '../../components/GlobalComponents/GlobalInputField';

const LoginScreen: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loginError, setLoginError] = useState('');

  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [schoolName, setSchoolName] = useState('');
  const [isTransitioning, setIsTransitioning] = useState(false);

  const { login } = useAuth();
  const toast = useRef<Toast>(null);

  const {
    states,
    districts,
    pincodes,
    schools,
    schoolData,
    selectedState,
    selectedDistrict,
    selectedPincode,
    selectedSchool,
    loading: apiLoading,
    error: apiError,
    handleStateChange,
    handleDistrictChange,
    handlePincodeChange,
    handleSchoolChange,
    fetchStates,
  } = useSchoolApiUrlSetup();

  const stepItems = [
    { label: 'School Setup', icon: 'pi-building' },
    { label: 'Login', icon: 'pi-sign-in' }
  ];

  useEffect(() => {
    const apiUrl = localStorage.getItem('API_BASE_URL');
    const storedSchoolName = localStorage.getItem('SCHOOL_NAME');

    if (apiUrl && storedSchoolName) {
      setActiveStep(1);
      setCompletedSteps([0]);
      setSchoolName(storedSchoolName);
    } else {
      setActiveStep(0);
      fetchStates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSchoolSubmit = useCallback(() => {
    if (!selectedSchool) {
      setLoginError('Please select a school');
      return;
    }

    const school = schoolData.find(s => s.id === selectedSchool);
    if (!school) {
      setLoginError('School data not found. Please try selecting again.');
      return;
    }

    localStorage.setItem('SCHOOL_ID', school.id);
    localStorage.setItem('SCHOOL_NAME', school.name);
    localStorage.setItem('API_BASE_URL', school.api_url);

    setSchoolName(school.name);
    setLoginError('');
    
    setIsTransitioning(true);
    setTimeout(() => {
      setCompletedSteps([0]);
      setActiveStep(1);
      setIsTransitioning(false);
    }, 300);
  }, [selectedSchool, schoolData]);

  const handleChangeSchool = useCallback(() => {
    localStorage.removeItem('API_BASE_URL');
    localStorage.removeItem('SCHOOL_ID');
    localStorage.removeItem('SCHOOL_NAME');

    setSchoolName('');
    setLoginError('');
    setCompletedSteps([]);
    
    setIsTransitioning(true);
    setTimeout(() => {
      setActiveStep(0);
      setIsTransitioning(false);
      fetchStates();
    }, 300);
  }, [fetchStates]);

  const validateForm = () => {
    let isValid = true;

    if (!email.trim()) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please enter your username or email',
        life: 3000
      });
      isValid = false;
    }

    if (!password.trim()) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please enter your password',
        life: 3000
      });
      isValid = false;
    }

    return isValid;
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');

    if (!validateForm()) {
      return;
    }

    try {
      await login(email.trim(), password);
    } catch (err: any) {
      const errorMsg = err.message || 'Login failed. Please check your credentials.';
      setLoginError(errorMsg);
      toast.current?.show({
        severity: 'error',
        summary: 'Login Failed',
        detail: errorMsg,
        life: 4000
      });
    }
  };

  const displayError = apiError || loginError;

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <div className="login-container">
        <div className="login-card">
          {/* Left Panel */}
          <div className="login-left animated-gradient">
            <div className="swirl-layer-1"></div>
            <div className="swirl-layer-2"></div>
            
            <div className="login-left-content">
              <div>
                <svg className="brand-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L12 12M12 12L12 22M12 12L22 12M12 12L2 12" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
                </svg>
              </div>
              
              <div>
                <p className="tagline">You can easily</p>
                <h1 className="main-heading">
                  Get access your personal hub for clarity and productivity
                </h1>
              </div>
            </div>
          </div>

          {/* Right Panel */}
          <div className="login-right">
            <div>
              <h2 className="form-title">Welcome to SkoolPillar</h2>
              <p className="form-subtitle">
                {activeStep === 0 ? 'First, select your school to get started' : 'Sign in to your account'}
              </p>
            </div>

            {/* Stepper */}
            <div className="custom-stepper-container">
              <div className="custom-stepper">
                <div className="step-connector">
                  <div 
                    className={`step-connector-progress ${completedSteps.length > 0 ? 'animating' : ''}`}
                    style={{ width: completedSteps.length > 0 ? '100%' : '0%' }}
                  />
                </div>

                {stepItems.map((step, index) => (
                  <div key={index} className="step-item">
                    <div 
                      className={`step-circle ${
                        completedSteps.includes(index) 
                          ? 'completed' 
                          : index === activeStep 
                          ? 'active' 
                          : 'inactive'
                      }`}
                      onClick={() => {
                        if (completedSteps.includes(index) && index === 0) {
                          handleChangeSchool();
                        }
                      }}
                    >
                      {completedSteps.includes(index) ? (
                        <div className="step-checkmark">
                          <svg viewBox="0 0 24 24">
                            <path d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      ) : (
                        <i className={`pi ${step.icon} step-icon`}></i>
                      )}
                      
                      {completedSteps.includes(index) && index === 0 && (
                        <div className="step-edit-icon">
                          <i className="pi pi-pencil"></i>
                        </div>
                      )}
                    </div>
                    <div className={`step-label ${
                      completedSteps.includes(index) 
                        ? 'completed' 
                        : index === activeStep 
                        ? 'active' 
                        : 'inactive'
                    }`}>
                      {step.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {displayError && (
              <Message severity="error" className='custominline' text={displayError} style={{ marginBottom: '1.5rem', width: '100%' }} />
            )}

            {/* School Setup Step */}
            {activeStep === 0 && (
              <div className={`form-content ${isTransitioning ? 'transitioning' : ''}`}>
                <div className="input-wrapper">
                  <GlobalDropdownField label="State" value={selectedState} onChange={handleStateChange} options={states} placeholder="Select your state" required disabled={apiLoading} icon="pi pi-map-marker" filter />
                </div>
                <div className="input-wrapper">
                  <GlobalDropdownField label="District" value={selectedDistrict} onChange={handleDistrictChange} options={districts} placeholder="Select your district" required disabled={apiLoading || districts.length <= 1} icon="pi pi-map" filter />
                </div>
                <div className="input-wrapper">
                  <GlobalDropdownField label="Pincode" value={selectedPincode} onChange={handlePincodeChange} options={pincodes} placeholder="Select your pincode" required disabled={apiLoading || pincodes.length <= 1} icon="pi pi-inbox" filter />
                </div>
                <div className="input-wrapper">
                  <GlobalDropdownField label="School" value={selectedSchool} onChange={handleSchoolChange} options={schools} placeholder="Select your school" required disabled={apiLoading || schools.length <= 1} icon="pi pi-building" filter />
                </div>

                <GlobalButton
                  label={apiLoading ? 'Loading...' : 'Continue to Login'}
                  onClick={handleSchoolSubmit}
                  variant="primary"
                  size="large"
                  fullWidth
                  disabled={apiLoading || !selectedSchool}
                  loading={apiLoading}
                  icon="pi pi-arrow-right"
                  iconPos="right"
                />
              </div>
            )}

            {/* Login Step */}
            {activeStep === 1 && (
              <div className={`form-content ${isTransitioning ? 'transitioning' : ''}`}>
                <div className="school-info-card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <i className="pi pi-building" style={{ color: '#3b82f6', fontSize: '1rem' }}></i>
                      <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#1e3a8a' }}>{schoolName}</span>
                    </div>
                    <button className="change-school-btn" onClick={handleChangeSchool}>Change</button>
                  </div>
                </div>

                <form onSubmit={handleLoginSubmit}>
                  <div className="input-wrapper">
                    <GlobalInputField
                      label="Username or Email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your username or email"
                      type="text"
                      required
                      icon="pi pi-user"
                      autoComplete="username"
                    />
                  </div>

                  <div className="input-wrapper">
                    <GlobalInputField
                      label="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      type="password"
                      required
                      icon="pi pi-lock"
                      autoComplete="current-password"
                    />
                  </div>

                  <div className="remember-forgot">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Checkbox inputId="remember" checked={rememberMe} onChange={(e) => setRememberMe(e.checked ?? false)} />
                      <label htmlFor="remember" style={{ color: '#4b5563', cursor: 'pointer' }}>Remember me</label>
                    </div>
                    <a href="#" className="forgot-link">Forgot Password?</a>
                  </div>

                  <GlobalButton
                    label={apiLoading ? 'Signing in...' : 'Sign In'}
                    type="submit"
                    variant="primary"
                    size="large"
                    fullWidth
                    loading={apiLoading}
                    disabled={apiLoading}
                    icon="pi pi-sign-in"
                    iconPos="left"
                  />

                  <p className="signup-text">
                    Don't have an account? <a href="#" className="signup-link">Contact Administrator</a>
                  </p>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginScreen;